"use client";

import { useState } from "react";
import Link from "next/link";
import { useMutation, useQuery } from "convex/react";
import { makeFunctionReference } from "convex/server";
import {
  AlertTriangle,
  ArrowRight,
  CircleCheck,
  CircleSlash,
  HelpCircle,
  Loader2,
  PauseCircle,
  Play,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { QuerySkeleton } from "@/components/ui/skeleton";
import { useTenantContext } from "@/components/tenant/TenantContextProvider";
import { LoadDetailView } from "./LoadDetailView";
import type { LoadDetailData } from "./load-detail-types";

const getLoadById = makeFunctionReference<
  "query",
  { tenantId: string; loadId: string },
  LoadDetailData | null
>("loads:getById");

/**
 * Mirrors convex/agents/public.ts loadAgentRoles — the mutation enforces this
 * server-side; the client only decides whether to show the control.
 */
const AGENT_RUN_ROLES = ["dispatcher", "manager", "admin"] as const;

export type LoadAgentRunStatus =
  | { type: "inProgress"; running: Array<{ name: string; startedAt: number }> }
  | { type: "completed" }
  | { type: "canceled" }
  | { type: "failed"; error: string }
  | { type: "unknown" };

export type LoadAgentRunsData = {
  runs: Array<{
    runId: string;
    workflowId: string;
    objective: string;
    requestedBy: string;
    createdAt: number;
    status: LoadAgentRunStatus;
  }>;
  approvals: Array<{
    _id: string;
    approvalId: string;
    title: string;
    summary: string;
    status: string;
    recommendedAction: string;
    createdAt: number;
  }>;
  decisions: Array<{
    _id: string;
    decisionId: string;
    decision: string;
    outcome: string | null;
    confidenceScore: number;
    riskScore: number;
    reasoningSummary: string;
    recommendedNextAction: string;
    requiredApproval: boolean;
    risks: string[];
    createdAt: number;
  }>;
};

const startLoadAgentWorkflow = makeFunctionReference<
  "mutation",
  { tenantId: string; loadId: string; objective: string },
  { workflowId: string }
>("agents/public:startLoadAgentWorkflow");

const listWorkflowRunsForLoad = makeFunctionReference<
  "query",
  { tenantId: string; loadId: string },
  LoadAgentRunsData
>("agents/public:listWorkflowRunsForLoad");

export function LoadDetailClient({
  tenantId,
  loadId,
}: {
  tenantId: string;
  loadId: string;
}) {
  // Fail closed: without a configured Convex backend there is no load data.
  // Show an explicit unavailable state — never a fabricated demo load.
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    return (
      <LoadDetailMessage
        title="Load data unavailable"
        body="The Convex backend is not configured for this environment, so this load cannot be displayed."
      />
    );
  }

  return <LiveLoadDetail tenantId={tenantId} loadId={loadId} />;
}

function LiveLoadDetail({ tenantId, loadId }: { tenantId: string; loadId: string }) {
  const data = useQuery(getLoadById, { tenantId, loadId });

  if (data === undefined) {
    return (
      <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-[220px_minmax(0,1fr)_340px] gap-4 px-6 py-4">
        <QuerySkeleton surface="savedViews" />
        <QuerySkeleton surface="loadDetail" />
        <QuerySkeleton surface="agentTimeline" />
      </div>
    );
  }

  if (data === null) {
    return (
      <LoadDetailMessage
        title="Load not found"
        body="This load does not exist in your workspace, or you do not have access to it."
      />
    );
  }

  return (
    <>
      <LoadDetailView data={data} />
      <div className="mx-auto max-w-3xl px-6 pb-10">
        <LoadAgentPanel
          tenantId={tenantId}
          loadId={loadId}
          loadReference={data.load.reference}
        />
      </div>
    </>
  );
}

/**
 * The real agent-invocation surface for a load: starts
 * agents/public:startLoadAgentWorkflow for THIS load and streams the run
 * status, approval packets, and decisions from
 * agents/public:listWorkflowRunsForLoad. Shared with the Orchestrator Console.
 */
export function LoadAgentPanel({
  tenantId,
  loadId,
  loadReference,
}: {
  tenantId: string;
  loadId: string;
  loadReference?: string;
}) {
  const tenantState = useTenantContext();
  const canRun =
    tenantState.status === "ready" &&
    (AGENT_RUN_ROLES as readonly string[]).includes(tenantState.tenant.role);

  const reference = loadReference ?? "this load";
  const [objective, setObjective] = useState(
    `Work ${reference} end to end: vet carrier options, price the lane, confirm tracking, chase documents, prepare billing, and draft customer comms. Hold every outbound action for approval.`,
  );
  const [pending, setPending] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const startWorkflow = useMutation(startLoadAgentWorkflow);
  const activity = useQuery(listWorkflowRunsForLoad, { tenantId, loadId });

  const startRun = async () => {
    const trimmed = objective.trim();
    if (!trimmed || pending) return;
    setPending(true);
    setStartError(null);
    try {
      await startWorkflow({ tenantId, loadId, objective: trimmed });
    } catch (error) {
      setStartError(error instanceof Error ? error.message : "Failed to start the agent workflow");
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="rounded-lg border border-border bg-card p-4" data-testid="load-agent-panel">
      <div className="flex items-center gap-2">
        <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-primary/10 text-primary">
          <Sparkles size={13} />
        </span>
        <span className="text-sm font-medium">Haulbase agent</span>
        <Badge variant="info">Live</Badge>
      </div>

      {canRun ? (
        <div className="mt-3">
          <label className="text-xs text-muted-foreground" htmlFor="agent-objective">
            Objective
          </label>
          <div className="mt-1 flex items-end gap-2">
            <textarea
              id="agent-objective"
              value={objective}
              onChange={(event) => setObjective(event.target.value)}
              rows={2}
              className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              type="button"
              onClick={startRun}
              disabled={pending || !objective.trim()}
              className="flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
            >
              {pending ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              Run agent
            </button>
          </div>
          {startError && (
            <p className="mt-2 flex items-center gap-1.5 text-[12.5px] text-red-600">
              <AlertTriangle size={13} /> {startError}
            </p>
          )}
        </div>
      ) : (
        <p className="mt-3 text-[12.5px] text-muted-foreground">
          Running the agent requires an active dispatcher, manager, or admin membership in this
          workspace.
        </p>
      )}

      <div className="mt-4">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Agent runs
        </div>
        {activity === undefined ? (
          <p className="mt-2 text-[12.5px] text-muted-foreground">Loading agent activity…</p>
        ) : activity.runs.length === 0 ? (
          <p className="mt-2 text-[12.5px] text-muted-foreground">
            No agent runs for this load yet. Start one above — every outbound action is drafted and
            held for approval.
          </p>
        ) : (
          <ol className="mt-2 space-y-2">
            {activity.runs.map((run) => (
              <li key={run.runId} className="rounded-md border border-border p-2.5">
                <div className="flex items-start justify-between gap-2">
                  <p className="flex-1 text-[12.5px]">{run.objective}</p>
                  <RunStatusBadge status={run.status} />
                </div>
                {run.status.type === "inProgress" && run.status.running.length > 0 && (
                  <ul className="mt-1.5 flex flex-wrap gap-1.5">
                    {run.status.running.map((step) => (
                      <li
                        key={`${run.runId}-${step.name}-${step.startedAt}`}
                        className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground"
                      >
                        <Loader2 size={11} className="animate-spin" /> {step.name}
                      </li>
                    ))}
                  </ul>
                )}
                {run.status.type === "failed" && (
                  <p className="mt-1.5 text-[12px] text-red-600">{run.status.error}</p>
                )}
                {run.status.type === "completed" && (
                  <Link
                    href="/inbox"
                    className="mt-1.5 inline-flex items-center gap-1 text-[12px] text-primary hover:underline"
                  >
                    Review resulting drafts &amp; approvals <ArrowRight size={12} />
                  </Link>
                )}
                <div className="mt-1.5 text-[11px] text-muted-foreground">
                  {new Date(run.createdAt).toLocaleString()} · requested by {run.requestedBy}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {activity !== undefined && activity.approvals.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Approval packets
          </div>
          <ol className="mt-2 space-y-2">
            {activity.approvals.map((approval) => (
              <li key={approval._id} className="rounded-md border border-border p-2.5">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[12.5px] font-medium">{approval.title}</span>
                  <Badge variant={approval.status === "pending" ? "warn" : "neutral"}>
                    {approval.status}
                  </Badge>
                </div>
                <p className="mt-1 text-[12px] text-muted-foreground">{approval.summary}</p>
                <Link
                  href="/inbox"
                  className="mt-1.5 inline-flex items-center gap-1 text-[12px] text-primary hover:underline"
                >
                  Open in inbox <ArrowRight size={12} />
                </Link>
              </li>
            ))}
          </ol>
        </div>
      )}

      {activity !== undefined && activity.decisions.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Decisions
          </div>
          <ol className="mt-2 space-y-2">
            {activity.decisions.map((decision) => (
              <li key={decision._id} className="rounded-md border border-border p-2.5">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[12.5px]">{decision.reasoningSummary}</span>
                  {decision.outcome === "deferred" ? (
                    <Badge variant="warn" className="shrink-0 gap-1">
                      <PauseCircle size={12} /> Deferred — AI budget
                    </Badge>
                  ) : (
                    <Badge variant={decision.requiredApproval ? "warn" : "neutral"} className="shrink-0">
                      {decision.decision}
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-[12px] text-muted-foreground">
                  Next: {decision.recommendedNextAction}
                </p>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  confidence {decision.confidenceScore} · risk {decision.riskScore} ·{" "}
                  {new Date(decision.createdAt).toLocaleString()}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}

function RunStatusBadge({ status }: { status: LoadAgentRunStatus }) {
  switch (status.type) {
    case "inProgress":
      return (
        <Badge variant="info" className="shrink-0 gap-1">
          <Loader2 size={12} className="animate-spin" /> Running
        </Badge>
      );
    case "completed":
      return (
        <Badge variant="good" className="shrink-0 gap-1">
          <CircleCheck size={12} /> Completed
        </Badge>
      );
    case "failed":
      return (
        <Badge variant="bad" className="shrink-0 gap-1">
          <AlertTriangle size={12} /> Failed
        </Badge>
      );
    case "canceled":
      return (
        <Badge variant="neutral" className="shrink-0 gap-1">
          <CircleSlash size={12} /> Canceled
        </Badge>
      );
    case "unknown":
      return (
        <Badge variant="neutral" className="shrink-0 gap-1">
          <HelpCircle size={12} /> Status unavailable
        </Badge>
      );
  }
}

function LoadDetailMessage({ title, body }: { title: string; body: string }) {
  return (
    <main className="mx-auto flex min-h-screen max-w-xl flex-col items-center justify-center px-6 py-12 text-center">
      <h1 className="text-lg font-semibold text-foreground">{title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
      <Link
        href="/loads"
        className="mt-5 rounded-md border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
      >
        Back to loads
      </Link>
    </main>
  );
}
